/**
 * Date formatting utilities that use Global Settings date format
 * Formats: DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD
 */

// Store the current date format (loaded from settings)
let currentDateFormat = 'DD/MM/YYYY';

/**
 * Set the global date format from settings
 * @param {string} format - The date format from Global Settings
 */
export const setDateFormat = (format) => {
  if (format && ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'].includes(format)) {
    currentDateFormat = format;
  }
};

/**
 * Get the current date format
 * @returns {string} The current date format
 */
export const getDateFormat = () => currentDateFormat;

/**
 * Format a date string or Date object according to Global Settings
 * @param {string|Date} dateInput - The date to format
 * @param {object} options - Formatting options
 * @param {boolean} options.includeTime - Whether to include time
 * @param {boolean} options.shortMonth - Use short month name (e.g., "Mar" instead of "March")
 * @returns {string} Formatted date string
 */
export const formatDate = (dateInput, options = {}) => {
  if (!dateInput) return '-';
  
  const { includeTime = false, shortMonth = true } = options;
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    
    if (isNaN(date.getTime())) return '-';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    let formatted;
    
    switch (currentDateFormat) {
      case 'MM/DD/YYYY':
        formatted = `${month}/${day}/${year}`;
        break;
      case 'YYYY-MM-DD':
        formatted = `${year}-${month}-${day}`;
        break;
      case 'DD/MM/YYYY':
      default:
        formatted = `${day}/${month}/${year}`;
        break;
    }
    
    if (includeTime) {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      formatted += ` ${hours}:${minutes}`;
    }
    
    return formatted;
  } catch (error) {
    console.error('Date formatting error:', error);
    return '-';
  }
};

/**
 * Format a date with month name (e.g., "14 Mar 2026" or "Mar 14, 2026")
 * @param {string|Date} dateInput - The date to format
 * @param {object} options - Formatting options
 * @param {boolean} options.shortMonth - Use short month name
 * @param {boolean} options.includeTime - Include time
 * @returns {string} Formatted date string with month name
 */
export const formatDateWithMonth = (dateInput, options = {}) => {
  if (!dateInput) return '-';
  
  const { shortMonth = true, includeTime = false } = options;
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    
    if (isNaN(date.getTime())) return '-';
    
    const day = date.getDate();
    const year = date.getFullYear();
    const monthNames = shortMonth 
      ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthName = monthNames[date.getMonth()];
    
    let formatted;
    
    // Respect the format preference for day/month order
    if (currentDateFormat === 'MM/DD/YYYY') {
      formatted = `${monthName} ${day}, ${year}`;
    } else {
      formatted = `${day} ${monthName} ${year}`;
    }
    
    if (includeTime) {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      formatted += ` ${hours}:${minutes}`;
    }
    
    return formatted;
  } catch (error) {
    console.error('Date formatting error:', error);
    return '-';
  }
};

/**
 * Get relative time description (e.g., "2 hours ago", "Yesterday")
 * @param {string|Date} dateInput - The date to describe
 * @returns {string} Relative time description
 */
export const getRelativeTime = (dateInput) => {
  if (!dateInput) return '-';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffSecs < 60) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return formatDateWithMonth(date);
  } catch (error) {
    return '-';
  }
};

export default {
  setDateFormat,
  getDateFormat,
  formatDate,
  formatDateWithMonth,
  getRelativeTime
};
